import { useEffect, useState } from 'react';
import { BellRing, Clock } from 'lucide-react';
import { api } from './api';

interface KycInfo { status: string; latest: { created_at: string } | null; canRemind?: boolean; remindAt?: string | null; reminders?: number }

const wait = (iso: string, now: number) => {
  const m = Math.max(0, Math.ceil((Date.parse(iso) - now) / 60_000));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`;
};

/**
 * Beside the client's KYC status while it is pending: after 24 hours with no decision they can remind the team,
 * then again every 24 hours. Before that, it says when the button will be available.
 */
export function KycReminder({ compact = false }: { compact?: boolean }) {
  const [k, setK] = useState<KycInfo | null>(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => api.get<KycInfo>('/v1/me/kyc').then(setK).catch(() => undefined);
  useEffect(() => { void load(); const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);
  useEffect(() => { if (k?.remindAt && !k.canRemind && Date.parse(k.remindAt) <= now) void load(); }, [now]);
  if (!k || k.status !== 'pending' || !k.latest) return null;
  async function remind() {
    setBusy(true); setMsg(null);
    try { await api.post('/v1/me/kyc/remind', {}); setMsg({ ok: true, text: 'Reminder sent. Our verification team has been notified. You can send another in 24 hours if it is still pending.' }); await load(); }
    catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : 'Could not send the reminder.' }); }
    finally { setBusy(false); }
  }
  const submitted = new Date(k.latest.created_at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return (
    <div className={`kyc-rem${compact ? ' compact' : ''}${k.canRemind ? ' ready' : ''}`} role="status">
      <span className="kyc-rem-ic" aria-hidden="true">{k.canRemind ? <BellRing /> : <Clock />}</span>
      <div className="kyc-rem-t">
        <b>{k.canRemind ? 'Still waiting for your verification?' : 'Your documents are being reviewed'}</b>
        <small>Submitted {submitted}{k.reminders ? ` · ${k.reminders} reminder${k.reminders === 1 ? '' : 's'} sent` : ''}{!k.canRemind && k.remindAt ? ` · You can remind the team in ${wait(k.remindAt, now)}` : ''}</small>
        {msg && <small className={msg.ok ? 'ok' : 'err'}>{msg.text}</small>}
      </div>
      {k.canRemind && <button className="btn sm" disabled={busy} onClick={() => void remind()}><BellRing />{busy ? 'Sending…' : 'Remind the team'}</button>}
    </div>
  );
}
